import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { normalizeCdtStudioCategoryKey } from "@CommonApi/merchant-settings";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import { PrimitiveAtom, useAtom, useAtomValue } from "jotai";

import { Highlights } from "./panels/Highlights/Highlights";
import { ProductDetails } from "./panels/ProductDetails/ProductDetails";
import { UnrangedItems } from "./panels/UnrangedItems/UnrangedItems";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { unrangedShelfIdAtom } from "./panels/UnrangedItems/store/atoms";
import { useEffect, useMemo } from "react";
import { Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { SidepanelButton } from "./panels/NewShelf/components/SidepanelButton";
import { ShelvesDetails } from "./panels/ShelvesDetails/ShelvesDetails";
import { NewShelf } from "./panels/NewShelf/NewShelf";
import { SpaceMix } from "./panels/SpaceMix/SpaceMix";
import { UnrangedContainers } from "./panels/UnrangedContainers/UnrangedContainers";
import { SidepanelToggleButton } from "./components/ToggleButton";
import { SidepanelLockButton } from "./components/LockButton";
import { CdtStudioPanel } from "src/pages/CdtStudio/components/CdtStudioPanel";
import { getCdtStudioAvailability } from "src/pages/CdtStudio/store/isCdtStudioEnabled";
import {
  getStudioCategoryConfiguration,
  getStudioCategoryConfigurationDebugInfo,
  type StudioCategoryIdentity,
} from "src/pages/CdtStudio/store/studioCategoryConfiguration";
import { useUser } from "src/atoms/user";
import {
  planogramSidepanelIsExpandedAtom,
  planogramSidepanelTabsLockedAtom,
  usePlanogramPanel,
} from "./store/hooks";
import { ProductsDetails } from "./panels/ProductsDetails/ProductsDetails";
import { planogramSelectedItemsAtom } from "src/components/PlanogramExtensions/store/atoms";
import { merchantSettingsAtom } from "src/components/FeatureFlagProvider/store/atoms";

interface Props {
  scaleX: number;
  scaleY: number;
  pogAtom: PrimitiveAtom<Pog>;
  isEditable: boolean;
  isPog: boolean;
  merchflowId?: number;
  basePogId?: number;
  currentStoreCode?: string;
  showProducts?: boolean;
  onGenerate?: () => Promise<unknown>;
  availableStores: ResponseMerchFlowApplyFilters["filter_config"] | null;
}

const categoryIdentityOf = (
  availableStores: ResponseMerchFlowApplyFilters["filter_config"] | null,
  merchantId: number,
  merchantName: string,
): StudioCategoryIdentity | undefined => {
  const identities = new Map<string, StudioCategoryIdentity>();
  availableStores?.stores.forEach(({ category_code, retailer_category_code }) => {
    const identity = {
      merchantId,
      merchantName,
      categoryGroup: retailer_category_code.trim(),
      categoryCode: category_code.trim(),
    };
    if (!identity.categoryGroup || !identity.categoryCode) return;
    const key = `${identity.categoryGroup.toLocaleUpperCase()}\u0000${identity.categoryCode.toLocaleUpperCase()}`;
    identities.set(key, identity);
  });
  return identities.size === 1 ? identities.values().next().value : undefined;
};

export const PlanogramPanel = ({
  scaleX,
  scaleY,
  pogAtom,
  isEditable,
  isPog,
  merchflowId,
  basePogId,
  currentStoreCode,
  availableStores,
  showProducts = true,
  onGenerate,
}: Props) => {
  const { t } = useTranslation("common");
  const [pog, setPog] = useAtom(pogAtom);
  const { tab, setTab } = usePlanogramPanel();
  const { user } = useUser();
  const categoryIdentity = useMemo(
    () => categoryIdentityOf(availableStores, user.merchantId, user.merchantName ?? ""),
    [availableStores, user.merchantId, user.merchantName],
  );
  // Merchant settings are the only configuration that can open the studio tab.
  const merchantSettings = useAtomValue(merchantSettingsAtom);
  const studioCategoryConfiguration = categoryIdentity
    ? getStudioCategoryConfiguration(categoryIdentity, merchantSettings?.cdtStudioConfigurations)
    : undefined;
  const canEditCdt = hasPermission(user.roleCode, PermissionCode.CDT_EDIT);
  const cdtStudioAvailability = getCdtStudioAvailability({
    isPog,
    merchflowId,
    hasCategoryIdentity: categoryIdentity !== undefined,
    hasCategoryConfiguration: studioCategoryConfiguration !== undefined,
    canEditCdt,
    merchantName: user.merchantName,
  });
  const showCdtStudio = cdtStudioAvailability.isAvailable;
  const cdtStudioUnavailableReason = cdtStudioAvailability.isAvailable
    ? undefined
    : cdtStudioAvailability.reason;
  const cdtStudioUnavailableMessage = cdtStudioUnavailableReason
    ? t(`cdtStudioTab.unavailable.${cdtStudioUnavailableReason}`)
    : undefined;

  useEffect(() => {
    if (!isPog || cdtStudioUnavailableReason === undefined) return;
    console.warn("[CDT Studio][disabled]", {
      reason: cdtStudioUnavailableReason,
      merchflowId,
      merchantId: user.merchantId,
      merchantName: user.merchantName,
      canEditCdt,
      categoryIdentity: categoryIdentity ?? null,
      categoryCandidates:
        availableStores?.stores.map(({ category_code, retailer_category_code }) => ({
          categoryGroup: retailer_category_code,
          categoryCode: category_code,
        })) ?? [],
      configuration: categoryIdentity
        ? getStudioCategoryConfigurationDebugInfo(categoryIdentity)
        : undefined,
    });
  }, [
    availableStores,
    canEditCdt,
    categoryIdentity?.categoryCode,
    categoryIdentity?.categoryGroup,
    cdtStudioUnavailableReason,
    isPog,
    merchflowId,
    user.merchantId,
    user.merchantName,
  ]);

  const [unrangedShelfId] = useAtom(unrangedShelfIdAtom);
  const [selectedItems] = useAtom(planogramSelectedItemsAtom);

  const [isExpanded, setIsExpanded] = useAtom(planogramSidepanelIsExpandedAtom);
  const [isLocked, setIsLocked] = useAtom(planogramSidepanelTabsLockedAtom);
  const isCdtStudioOpen =
    isExpanded &&
    tab === "CDT_STUDIO" &&
    showCdtStudio &&
    categoryIdentity !== undefined &&
    studioCategoryConfiguration !== undefined;

  useEffect(() => {
    if (unrangedShelfId) {
      setTab("UNRANGED_ITEMS");
    }
  }, [unrangedShelfId, setTab]);

  useEffect(() => {
    if (tab === "PRODUCT_DETAILS" && selectedItems.length === 0) {
      setTab("UNRANGED_ITEMS", true);
    }
  }, [tab, selectedItems, setTab]);

  const hasProductSelection = selectedItems.length > 0;

  return (
    <Flex>
      {isExpanded && (
        <Flex
          data-testid="planogram-tab-content"
          flexGrow
          borderLeft={`1px solid ${Color.primary}`}
          width={tab === "CDT_STUDIO" ? "clamp(560px, 48vw, 720px)" : "322px"}
          minWidth="0"
        >
          {isCdtStudioOpen && (
            <CdtStudioPanel
              key={`${categoryIdentity.merchantId}/${normalizeCdtStudioCategoryKey(categoryIdentity.categoryGroup)}/${normalizeCdtStudioCategoryKey(categoryIdentity.categoryCode)}`}
              pogAtom={pogAtom}
              isEditable={isEditable}
              merchflowId={merchflowId}
              basePogId={basePogId}
              categoryIdentity={categoryIdentity}
              categoryConfiguration={studioCategoryConfiguration}
              onClose={() => setTab("HIGHLIGHTS")}
              onGenerate={onGenerate}
            />
          )}

          {tab === "UNRANGED_ITEMS" && (
            <UnrangedItems
              pogAtom={pogAtom}
              scaleX={scaleX}
              scaleY={scaleY}
              merchflowId={merchflowId}
              currentStoreCode={currentStoreCode}
              availableStores={availableStores}
            />
          )}

          {tab === "UNRANGED_CONTAINERS" && <UnrangedContainers pogAtom={pogAtom} />}

          {tab === "PRODUCT_DETAILS" &&
            (selectedItems.length > 1 ? (
              <ProductsDetails pog={pog} setPog={setPog} />
            ) : (
              <ProductDetails pog={pog} setPog={setPog} />
            ))}

          {tab === "HIGHLIGHTS" && <Highlights merchflowId={merchflowId} pogAtom={pogAtom} />}

          {tab === "FIXTURES_DETAILS" && (
            <ShelvesDetails
              pogAtom={pogAtom}
              isShelfWeightEnabled={Boolean(merchantSettings?.shelfWeightEnabled)}
            />
          )}

          {tab === "ADD_EDIT_FIXTURE" && <NewShelf pogAtom={pogAtom} />}

          {tab === "CDT_STUDIO" && !showCdtStudio && isPog && (
            <Flex column gap="8px" padding="16px">
              <Text variant="h5">{t("cdtStudioTab.tabLabel")}</Text>
              <Text variant="body3" color={Color.textSecondary}>
                {cdtStudioUnavailableMessage}
              </Text>
            </Flex>
          )}

          {tab === "SPACE_MIX" && <SpaceMix pogAtom={pogAtom} isPog={isPog} />}
        </Flex>
      )}

      <Flex
        width="28px"
        minWidth="28px"
        column
        align="between"
        borderLeft={`1px solid ${Color.primary}`}
        background={Color.greenSmokeDisabled}
      >
        <Flex column>
          {isPog && showProducts && (
            <>
              <SidepanelButton
                tab="PRODUCT_DETAILS"
                setTab={setTab}
                currentTab={tab}
                isDisabled={!hasProductSelection}
              >
                Product
              </SidepanelButton>

              <SidepanelButton tab="UNRANGED_ITEMS" setTab={setTab} currentTab={tab}>
                Unranged Items
              </SidepanelButton>

              <SidepanelButton tab="HIGHLIGHTS" setTab={setTab} currentTab={tab}>
                Highlights
              </SidepanelButton>
            </>
          )}

          {!isPog && showProducts && (
            <SidepanelButton tab="UNRANGED_CONTAINERS" setTab={setTab} currentTab={tab}>
              Unranged Containers
            </SidepanelButton>
          )}

          {showProducts && (
            <SidepanelButton tab="SPACE_MIX" setTab={setTab} currentTab={tab}>
              Space Mix
            </SidepanelButton>
          )}

          <SidepanelButton tab="FIXTURES_DETAILS" setTab={setTab} currentTab={tab}>
            Fixtures
          </SidepanelButton>

          <SidepanelButton tab="ADD_EDIT_FIXTURE" setTab={setTab} currentTab={tab}>
            Add / Edit Fixture
          </SidepanelButton>

          {isPog && (
            <SidepanelButton
              tab="CDT_STUDIO"
              setTab={setTab}
              currentTab={tab}
              isDisabled={!showCdtStudio}
              disabledReason={cdtStudioUnavailableMessage}
            >
              {t("cdtStudioTab.tabLabel")}
            </SidepanelButton>
          )}
        </Flex>

        <Flex column>
          <SidepanelLockButton isLocked={isLocked} setIsLocked={setIsLocked} />
          <SidepanelToggleButton isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
        </Flex>
      </Flex>
    </Flex>
  );
};
